<template>
    <div class="embed-dialog-container">
      <v-card class="embed-dialog" elevation="3">
        <div class="pa-6">
          <!-- Header -->
          <div class="d-flex align-center mb-6">
            <v-icon color="primary" class="mr-2">mdi-code-tags</v-icon>
            <h2 class="text-h6 font-weight-medium">Embed Conversation</h2>
          </div>

          <!-- Description -->
          <p class="text-body-2 text-grey-darken-1 mb-6">
            Add this conversation to your website or blog
          </p>

          <!-- Embed Code -->
          <div class="embed-code-container mb-6">
            <v-textarea
              v-model="embedCode"
              readonly
              outlined
              auto-grow
              rows="3"
              hide-details
              class="embed-input font-monospace"
              :class="{ 'copied': isCopied }"
            >
              <template v-slot:append>
                <v-btn
                  icon
                  @click="copyEmbedCode"
                  class="copy-button"
                  :color="isCopied ? 'success' : 'grey darken-1'"
                >
                  <v-icon>{{ isCopied ? 'mdi-check' : 'mdi-content-copy' }}</v-icon>
                </v-btn>
              </template>
            </v-textarea>
            <v-scale-transition>
              <div v-if="isCopied" class="copied-tooltip">
                Copied!
              </div>
            </v-scale-transition>
          </div>

          <!-- Customization Options -->
          <div class="embed-options mb-6">
            <p class="text-body-2 font-weight-medium text-grey-darken-2 mb-3">
              Customize
            </p>

            <v-row>
              <v-col cols="6">
                <v-text-field
                  v-model="embedWidth"
                  label="Width"
                  type="text"
                  outlined
                  dense
                  hide-details
                  class="mb-3"
                  @input="updateEmbedCode"
                ></v-text-field>
              </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model="embedHeight"
                  label="Height"
                  type="text"
                  outlined
                  dense
                  hide-details
                  class="mb-3"
                  @input="updateEmbedCode"
                ></v-text-field>
              </v-col>
            </v-row>
          </div>

          <!-- Preview -->
          <div class="embed-preview-container mb-6">
            <p class="text-body-2 font-weight-medium text-grey-darken-2 mb-3">
              Preview
            </p>
            <div class="embed-preview">
              <iframe
                :src="previewSrc"
                width="100%"
                :height="previewHeight"
                frameborder="0"
                class="preview-iframe"
              ></iframe>
            </div>
          </div>

          <!-- Close Button -->
          <div class="d-flex justify-end">
            <v-btn
              text
              color="primary"
              @click="$emit('close')"
            >
              Close
            </v-btn>
          </div>
        </div>
      </v-card>
    </div>
  </template>

  <script>
  export default {
    name: 'EmbedDialog',

    props: {
      shareUrl: {
        type: String,
        required: true
      }
    },

    data() {
      return {
        embedCode: '',
        isCopied: false,
        embedWidth: '100%',
        embedHeight: '100%',
        autoplay: false
      }
    },

    computed: {
      embedSrc() {
        let url = this.shareUrl;

        if (this.autoplay) {
          // Add autoplay parameter if needed
          url += (url.includes('?') ? '&' : '?') + 'autoplay=true';
        }

        return url;
      },

      previewSrc() {
        return this.embedSrc;
      },

      previewHeight() {
        // Set a reasonable fixed height for the preview
        return '300px';
      }
    },

    mounted() {
      this.updateEmbedCode();
    },

    methods: {
      updateEmbedCode() {
        this.embedCode = `<iframe src="${this.shareUrl}" style="position:fixed; top:0; left:0; bottom:0; right:0; width:${this.embedWidth}; height:${this.embedHeight}; border:none; margin:0; padding:0; overflow:hidden; z-index:999999;">Your browser doesn't support iframes</iframe>`;
      },
      async copyEmbedCode() {
        try {
          await navigator.clipboard.writeText(this.embedCode);
          this.isCopied = true;
          setTimeout(() => {
            this.isCopied = false;
          }, 2000);

          this.$emit('copied');
        } catch (err) {
          console.error('Failed to copy code:', err);
          this.fallbackCopyTextToClipboard(this.embedCode);
        }
      },

      fallbackCopyTextToClipboard(text) {
        const textArea = document.createElement("textarea");
        textArea.value = text;

        // Make the textarea out of viewport
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        textArea.style.top = "-999999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        try {
          const successful = document.execCommand('copy');
          if (successful) {
            this.isCopied = true;
            setTimeout(() => {
              this.isCopied = false;
            }, 2000);

            this.$emit('copied');
          }
        } catch (err) {
          console.error('Fallback: Unable to copy', err);
        }

        document.body.removeChild(textArea);
      }
    }
  }
  </script>

  <style scoped>
  .embed-dialog-container {
    width: 100%;
  }

  .embed-dialog {
    max-width: 500px;
    margin: 0 auto;
    border-radius: 12px !important;
  }

  .embed-code-container {
    position: relative;
  }

  .embed-input {
    transition: all 0.3s ease;
  }

  .font-monospace >>> textarea {
    font-family: monospace !important;
    font-size: 13px;
  }

  .embed-input.copied >>> .v-input__slot {
    border-color: var(--v-success-base) !important;
  }

  .copy-button {
    transition: all 0.3s ease !important;
  }

  .copied-tooltip {
    position: absolute;
    top: -24px;
    right: 0;
    background: var(--v-success-base);
    color: white;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 12px;
  }

  .embed-preview-container {
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid rgba(0,0,0,0.12);
  }

  .embed-preview {
    width: 100%;
    height: 300px;
    overflow: hidden;
  }

  .preview-iframe {
    transform: scale(0.9);
    transform-origin: 0 0;
    width: 111.11% !important; /* 100/0.9 to counter the scale */
  }
  </style>
